// src/layouts/Sidebar.js
import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";
import userAvatar from "../assets/img/img1.jpg";
import logo from "../assets/img/logo1.png";
import '../assets/css/sidebar.css';
import { getDashboardMenu } from "../data/Menu";
import { useAuth } from "../hooks/useAuth";

const Sidebar = () => {
  const [menuData, setMenuData] = useState([]);
  const [userName, setUserName] = useState("Administrateur");
  const [userTitle, setUserTitle] = useState("Utilisateur");
  const [redirecting, setRedirecting] = useState(false);
  const [userRolesList, setUserRolesList] = useState([]);
  const [userData, setUserData] = useState(null);
  
  const { isAuthenticated, user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setRedirecting(true);
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const loadUserData = () => {
      // ✅ Récupérer les données utilisateur complètes depuis sessionStorage
      const storedUser = sessionStorage.getItem('user_data');
      let userDataComplete = null;
      
      if (storedUser) {
        try {
          userDataComplete = JSON.parse(storedUser);
          if (userDataComplete.user) {
            userDataComplete = {
              ...userDataComplete.user,
              ...userDataComplete
            };
          }
        } catch (e) {
          console.error('Erreur parsing user data:', e);
        }
      }
      
      if (!userDataComplete && user) {
        userDataComplete = {
          user_id: user.user_id,
          id_marche: user.id_marche,
          role_id: user.role_id,
          user_scope: user.user_scope || 'global',
          full_name: user.full_name,
          admin_fname: user.admin_fname,
          admin_lname: user.admin_lname,
          ...user
        };
      }
      
      setUserData(userDataComplete);
      
      const userRoles = getUserRoles();
      setUserRolesList(userRoles);
      
      const menu = getDashboardMenu(userDataComplete, userRoles);
      setMenuData(menu);
      
      if (userDataComplete) {
        const fullName = `${userDataComplete.admin_fname || ''} ${userDataComplete.admin_lname || ''}`.trim() || 
                        `${userDataComplete.full_name || ''}`.trim() ||
                        "Administrateur";
        setUserName(fullName);
        setUserTitle(getUserTitle(userDataComplete, userRoles));
      }
    };
    
    loadUserData();
  }, [user]);

  const getUserRoles = () => {
    const storedRoles = sessionStorage.getItem('userRoles');
    if (storedRoles) {
      try {
        const parsed = JSON.parse(storedRoles);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      } catch (e) {
        console.error('Erreur parsing user roles:', e);
      }
    }
    
    const storedUser = sessionStorage.getItem('user_data');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.userRoles && Array.isArray(userData.userRoles) && userData.userRoles.length > 0) {
          return userData.userRoles;
        }
        if (userData.role) {
          return [userData.role];
        }
      } catch (e) {
        console.error('Erreur parsing user roles from user_data:', e);
      }
    }
    
    if (user?.roles && Array.isArray(user.roles) && user.roles.length > 0) {
      return user.roles;
    }
    
    if (user?.role) {
      return [user.role];
    }
    
    // ✅ Si aucun rôle trouvé, retourner un rôle par défaut
    return [{ name: 'user', id: null }];
  }

  // ✅ Vérifier si l'utilisateur a un marché
  const hasMarket = () => {
    return !!(userData?.id_marche);
  };

  // ✅ Vérifier si l'utilisateur a un rôle null (cas spécial)
  const hasNullRole = () => {
    return userData?.role_id === null || userData?.role_id === undefined;
  };

  // ✅ Vérifier si l'utilisateur est Admin Entité (1002) - hors marché
  const isAdminEntite = () => {
    return userData?.role_id === 1002;
  };

  // ✅ Vérifier si l'utilisateur est Admin Principal (1003) - consolidé
  const isAdminPrincipal = () => {
    return userData?.role_id === 1003;
  };

  // ✅ Vérifier si l'utilisateur est Super Admin
  const isSuperAdmin = () => {
    return userRolesList.some(r => r.name === 'super_admin') || userData?.role_id === 1001;
  };

  // ✅ Fonction pour obtenir le badge du rôle avec distinction Agent simple / Agent de marché
  const getRoleBadge = (roleId, roles, hasMarket) => {
    const hasSuperAdmin = roles?.some(r => r.name === 'super_admin') || false;
    
    if (hasSuperAdmin) {
      return { label: 'Super Admin', color: 'danger', icon: 'ri-admin-line' };
    }
    
    // ✅ Cas spécial : rôle null
    if (roleId === null || roleId === undefined) {
      return { label: 'Utilisateur', color: 'secondary', icon: 'ri-user-line' };
    }
    
    switch(roleId) {
      case 1002:
        return { label: 'Admin Economie', color: 'primary', icon: 'ri-building-4-line' };
      case 1003:
        return { label: 'Admin Principal', color: 'primary', icon: 'ri-building-line' };
      case 1005:
        return { label: 'Admin Marché', color: 'success', icon: 'ri-store-3-line' };
      case 1007:
        return { label: 'Superviseur', color: 'warning', icon: 'ri-eye-line' };
      case 1012:
        if (hasMarket) {
          return { label: userData?.admin_fonction, color: 'info', icon: 'ri-store-2-line' };
        }
        return { label: userData?.admin_fonction, color: 'info', icon: 'ri-user-star-line' };
      case 1013:
        if (hasMarket) {
          return { label: userData?.admin_fonction, color: 'warning', icon: 'ri-store-2-line' };
        }
        return { label: userData?.admin_fonction, color: 'secondary', icon: 'ri-user-voice-line' };
      default:
        return { label: 'Utilisateur', color: 'secondary', icon: 'ri-user-line' };
    }
  };

  // ✅ Fonction pour obtenir le titre du rôle
  const getUserTitle = (userData, roles) => {
    const hasSuperAdmin = roles.some(r => r.name === 'super_admin');
    const roleId = userData?.role_id;
    const hasMarket = !!userData?.id_marche;
    
    if (hasSuperAdmin) {
      return "Super Administrateur";
    }
    
    // ✅ Cas spécial : rôle null
    if (roleId === null || roleId === undefined) {
      return "Utilisateur";
    }
    
    switch(roleId) {
      case 1002:
        return "Administrateur Entité (Hors Marché)";
      case 1003:
        return "Admin Principal (ODD)";
      case 1005:
        return "Administrateur Marché";
      case 1007:
        return "Superviseur Marché";
      case 1012:
        return hasMarket ? "Agent de Marché" : "Agent de Collecte (Hors Marché)";
      case 1013:
        return hasMarket ? "Collecteur de Marché" : "Collecteur (Hors Marché)";
      default:
        return "Utilisateur";
    }
  }

  const getProfileLink = () => {
    const hasSuperAdmin = userRolesList.some(r => r.name === 'super_admin');
    const roleId = userData?.role_id;
    
    if (hasSuperAdmin) {
      return "/superAdmin/profil";
    }
    
    // ✅ Cas spécial : rôle null
    if (roleId === null || roleId === undefined) {
      return "/dashboard";
    }
    
    if (roleId === 1002) {
      return "/agent/profil";
    }
    
    if (roleId === 1003) {
      return "/agent/profil";
    }
    
    if (roleId === 1005 || roleId === 1007) {
      return "/agent/profil";
    }
    
    if (roleId === 1012 || roleId === 1013) {
      return "/agent/profil";
    }
    
    return "/dashboard";
  }

  const toggleFooterMenu = (e) => {
    e.preventDefault();
    let parent = e.target.closest(".sidebar");
    if (parent) {
      parent.classList.toggle("footer-menu-show");
    }
  }

  const getProfileMenu = () => {
    const handleLogout = async () => {
      await logout();
      navigate('/');
    };
    
    const hasSuperAdmin = userRolesList.some(r => r.name === 'super_admin');
    const roleId = userData?.role_id;
    const hasMarket = !!userData?.id_marche;
    const isAdminEntite = roleId === 1002;
    const isAdminPrincipal = roleId === 1003;
    const isNullRole = roleId === null || roleId === undefined;

    // ✅ Rôle null - Menu par défaut
    if (isNullRole) {
      return (
        <>
          <hr />
          <nav className="nav flex-column">
            <Link style={{color:'white'}} to="/dashboard" className="nav-link">
              <i className="ri-dashboard-line me-2"></i>
              Tableau de Bord
            </Link>
            <Link style={{color:'white'}} to="/contribuables/CollectesUsagers" className="nav-link">
              <i className="ri-history-line me-2"></i>
              Mes Collectes
            </Link>
            <Link style={{color:'white'}} to="/contribuables/Rapport" className="nav-link">
              <i className="ri-file-chart-line me-2"></i>
              Mes Rapports
            </Link>
          </nav>
          
          <hr className="my-2" />
          <Link style={{color:'white'}} to="/pages/Faq" className="nav-link">
            <i className="ri-question-line me-2"></i>
            Centre d'Aide
          </Link>
          
          <nav className="nav flex-column">
            <button onClick={handleLogout} className="nav-link text-danger border-0 bg-transparent text-start">
              <i className="ri-logout-box-r-line me-2"></i>
              Déconnexion
            </button>
          </nav>
        </>
      );
    }

    // ✅ Super Admin
    if (hasSuperAdmin) {
      return (
        <>
          <hr />
          <nav className="nav flex-column">
            <Link to="/superAdmin/profil" style={{color:'white'}} className="nav-link">
              <i className="ri-user-line me-2"></i>
              Mon Profil
            </Link>
            <Link style={{color:'white'}} to="/superAdmin/Collectes" className="nav-link">
              <i className="ri-history-line me-2"></i>
              Collectes
            </Link>
            <Link to="/superAdmin/Rapport" className="nav-link">
              <i className="ri-file-chart-line me-2"></i>
              Rapports
            </Link>
          </nav>
          
          <hr className="my-2" />
          <Link style={{color:'white'}} to="/pages/Faq" className="nav-link">
            <i className="ri-question-line me-2"></i>
            Centre d'Aide
          </Link>
          
          <nav className="nav flex-column">
            <button onClick={handleLogout} className="nav-link text-danger border-0 bg-transparent text-start">
              <i className="ri-logout-box-r-line me-2"></i>
              Déconnexion
            </button>
          </nav>
        </>
      );
    } 
    
    // ✅ Admin Entité (1002) - Hors Marché
    else if (isAdminEntite) {
      return (
        <>
         <hr className="my-2" />
          <nav className="nav flex-column">
            <Link to="/agent/profil" style={{color:'white'}} className="nav-link">
              <i className="ri-user-line me-2"></i>
              Mon Profil
            </Link>
            <Link style={{color:'white'}} to="/contribuables/CollectesUsagers" className="nav-link">
              <i className="ri-history-line me-2"></i>
              Collectes (Hors Marché)
            </Link>
            <Link to="/contribuables/Rapport" className="nav-link">
              <i className="ri-file-chart-line me-2"></i>
              Rapports (Hors Marché)
            </Link>
            <Link to="/agent/contribuables/liste" className="nav-link">
              <i className="ri-user-follow-line me-2"></i>
              Contribuables
            </Link>
          </nav>
          
          <hr className="my-2" />
          {/* <Link style={{color:'white'}} to="/pages/Faq" className="nav-link">
            <i className="ri-question-line me-2"></i>
            Centre d'Aide
          </Link> */}
          
          <nav className="nav flex-column">
            <button onClick={handleLogout} className="nav-link text-danger border-0 bg-transparent text-start">
              <i className="ri-logout-box-r-line me-2"></i>
              Déconnexion
            </button>
          </nav>
        </>
      );
    }
    
    // ✅ Admin Principal (1003) - Consolidé
    else if (isAdminPrincipal) {
      return (
        <>
         
          <nav className="nav flex-column">
            <Link to="/agent/profil" style={{color:'white'}} className="nav-link">
              <i className="ri-user-line me-2"></i>
              Mon Profil
            </Link>
            <Link style={{color:'white'}} to="/consolidated/dashboard" className="nav-link">
              <i className="ri-dashboard-line me-2"></i>
              Dashboard Consolidé
            </Link>
            <Link style={{color:'white'}} to="/contribuables/CollectesUsagers" className="nav-link">
              <i className="ri-history-line me-2"></i>
              Collectes Consolidées
            </Link>
            <Link to="/contribuables/Rapport" className="nav-link">
              <i className="ri-file-chart-line me-2"></i>
              Rapports Consolidés
            </Link>
            <Link to="/consolidated/salubrite" className="nav-link">
              <i className="ri-recycle-line me-2"></i>
              Taxe Salubrité
            </Link>
          </nav>
          
        
          {/* <Link style={{color:'white'}} to="/pages/Faq" className="nav-link">
            <i className="ri-question-line me-2"></i>
            Centre d'Aide
          </Link> */}
          
          <nav className="nav flex-column">
            <button onClick={handleLogout} className="nav-link text-danger border-0 bg-transparent text-start">
              <i className="ri-logout-box-r-line me-2"></i>
              Déconnexion
            </button>
          </nav>
        </>
      );
    }
    
    // ✅ Admin Marché (1005) ou Superviseur (1007)
    else if (roleId === 1005 || roleId === 1007) {
      return (
        <>
         <br className="my-2" /> <br className="my-2" /> <br className="my-2" />
          
          <nav className="nav flex-column">
            <Link to="/agent/profil" style={{color:'white'}} className="nav-link">
              <i className="ri-user-line me-2"></i>
              Mon Profil
            </Link>
            <Link style={{color:'white'}} to="/contribuables/CollectesUsagers" className="nav-link">
              <i className="ri-history-line me-2"></i>
              Collectes du Marché
            </Link>
            <Link to="/contribuables/Rapport" className="nav-link">
              <i className="ri-file-chart-line me-2"></i>
              Rapports du Marché
            </Link>
          </nav>
          
          <hr className="my-2" />
          {/* <Link style={{color:'white'}} to="/pages/Faq" className="nav-link">
            <i className="ri-question-line me-2"></i>
            Centre d'Aide
          </Link> */}
          
          <nav className="nav flex-column">
            <button onClick={handleLogout} className="nav-link text-danger border-0 bg-transparent text-start">
              <i className="ri-logout-box-r-line me-2"></i>
              Déconnexion
            </button>
          </nav>
        </>
      );
    } 
    
    // ✅ Agent (1012) ou Collecteur (1013)
    else if (roleId === 1012 || roleId === 1013) {
      const isMarketAgent = hasMarket && (roleId === 1012 || roleId === 1013);
      
      // ✅ Si c'est un agent hors marché (id_marche = null)
      if (!isMarketAgent) {
        return (
          <>
          <hr className="my-2" />
            <nav className="nav flex-column">
              <Link style={{color:'white'}} to="/agent/profil" className="nav-link">
                <i className="ri-user-line me-2"></i>
                Mon Profil
              </Link>
              <Link style={{color:'white'}} to="/contribuables/CollectesUsagers" className="nav-link">
                <i className="ri-history-line me-2"></i>
                Mes Collectes
              </Link>
              <Link style={{color:'white'}} to="/contribuables/Rapport" className="nav-link">
                <i className="ri-file-chart-line me-2"></i>
                Mes Rapports
              </Link>
              <Link style={{color:'white'}} to="/agent/contribuables" className="nav-link">
                <i className="ri-user-follow-line me-2"></i>
                Mes Contribuables
              </Link>
            </nav>
            
            <hr className="my-2" />
            
            <nav className="nav flex-column">
              {/* <Link style={{color:'white'}} to="/pages/Faq" className="nav-link">
                <i className="ri-question-line me-2"></i>
                Centre d'Aide
              </Link> */}
              <button onClick={handleLogout} className="nav-link text-danger border-0 bg-transparent text-start">
                <i className="ri-logout-box-r-line me-2"></i>
                Déconnexion
              </button>
            </nav>
          </>
        );
      }
      
      // ✅ Agent de marché (id_marche != null)
      return (
        <>
        <hr className="my-2" />
          <nav className="nav flex-column">
            <Link style={{color:'white'}} to="/agent/profil" className="nav-link">
              <i className="ri-user-line me-2"></i>
              Mon Profil
            </Link>
            <Link style={{color:'white'}} to="/contribuables/CollectesUsagers" className="nav-link">
              <i className="ri-history-line me-2"></i>
              Collectes du Marché
            </Link>
            <Link style={{color:'white'}} to="/contribuables/Rapport" className="nav-link">
              <i className="ri-file-chart-line me-2"></i>
              Rapports du Marché
            </Link>
          </nav>
          
          <hr className="my-2" />
          
          <nav className="nav flex-column">
            <Link style={{color:'white'}} to="/pages/Faq" className="nav-link">
              <i className="ri-question-line me-2"></i>
              Centre d'Aide
            </Link>
            <button onClick={handleLogout} className="nav-link text-danger border-0 bg-transparent text-start">
              <i className="ri-logout-box-r-line me-2"></i>
              Déconnexion
            </button>
          </nav>
        </>
      );
    }
    
    // ✅ Fallback
    return (
      <>
        <nav className="nav flex-column">
          <Link style={{color:'white'}} to="/dashboard" className="nav-link">
            <i className="ri-dashboard-line me-2"></i>
            Tableau de Bord
          </Link>
          <button onClick={handleLogout} className="nav-link text-danger border-0 bg-transparent text-start">
            <i className="ri-logout-box-r-line me-2"></i>
            Déconnexion
          </button>
        </nav>
      </>
    );
  }

  if (redirecting || authLoading) {
    return (
      <div className="sidebar" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  // ✅ Récupérer les infos pour les badges
  const roleBadge = getRoleBadge(userData?.role_id, userRolesList, !!userData?.id_marche);
  
  // ✅ Variables pour l'affichage des badges de marché
  const hasMarketValue = hasMarket();
  const isAdminEntiteValue = isAdminEntite();
  const isAdminPrincipalValue = isAdminPrincipal();
  const isSuperAdminValue = isSuperAdmin();
  const isNullRoleValue = hasNullRole();

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <Link to="/dashboard" className="sidebar-logo">
          <img src={logo} alt="SyncTaxe Logo" className="sidebar-logo-img" />
        </Link>
      </div>
      
      <PerfectScrollbar className="sidebar-body">
        <SidebarMenu menuData={menuData} />
      </PerfectScrollbar>
      
      <div className="sidebar-footer">
        <div className="sidebar-footer-top">
          <div className="sidebar-footer-thumb">
            <img src={userAvatar} alt="Avatar utilisateur" />
            <div className="status-indicator bg-success"></div>
          </div>
          <div className="sidebar-footer-body">
            <h6 className="mb-1" style={{fontSize:'12px'}}>
              <Link to={getProfileLink()} style={{color: '#fff', textDecoration: 'none'}}>
                {userName}
              </Link>
            </h6>
            {/* ✅ Badge de rôle avec couleur et icône + statut Marché/Hors Marché sur la même ligne */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
  {/* Badge du rôle */}
  <span className={`badge bg-${roleBadge.color} rounded-pill px-2 py-1`} style={{fontSize: '9px', fontWeight: '600'}}>
    <i className={`${roleBadge.icon} me-1`} style={{fontSize: '8px'}}></i>
    {roleBadge.label}
  </span>
  
  {/* Badge de statut (Marché/Hors Marché/Entité/Consolidé) */}
  {(() => {
    if (isAdminEntiteValue) {
      return (
        <></>
      );
    }
    if (isAdminPrincipalValue) {
      return (
        <></>
      );
    }
    if (!isNullRoleValue && !isSuperAdminValue && !isAdminEntite) {
      return (
        <span className={`badge ${hasMarketValue ? 'bg-success' : 'bg-secondary'} rounded-pill px-2 py-1`} style={{fontSize: '7px'}}>
          <i className={hasMarketValue ? 'ri-store-2-line' : 'ri-store-off-line'} me-1></i>
          {hasMarketValue ? 'Marché' : 'Hors Marché'}
        </span>
      );
    }
    return null;
  })()}
</div>
          </div>
          <Link 
            onClick={toggleFooterMenu} 
            to="#" 
            className="dropdown-link"
            title="Menu profil"
          >
            <i className="ri-arrow-down-s-line"></i>
          </Link>
        </div>
       
        
        <div className="sidebar-footer-menu">
          {getProfileMenu()}
          
          <div className="sidebar-footer-menu-footer">
            <small className="text-muted">
              <i className="ri-time-line me-1"></i>
              {new Date().toLocaleString('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// SIDEBAR MENU COMPONENT
// ============================================================
const SidebarMenu = ({ menuData }) => {
  const toggleMenu = (e) => {
    e.preventDefault();
    let parent = e.target.closest('.nav-group');
    if (parent) {
      parent.classList.toggle('show');
    }
  }

  const renderMenuItem = (item) => {
    if (item.submenu && item.submenu.length > 0) {
      return (
        <div className="nav-group" key={item.label}>
          <div className="nav-label text-white" onClick={toggleMenu}>
            <i className={`${item.icon} me-2`}></i>
            {item.label}
          </div>
          <ul className="nav nav-sidebar">
            {item.submenu.map((subItem, index) => (
              <li key={index} className="nav-item">
                <NavLink 
                  to={subItem.link} 
                  className={({ isActive }) => 
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  <i className={subItem.icon}></i> 
                  <span>{subItem.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      );
    }
    
    return (
      <div className="nav-group" key={item.label}>
        <NavLink 
          to={item.link} 
          className={({ isActive }) => 
            isActive ? "nav-label nav-label-clickable text-white active" : "nav-label nav-label-clickable text-white"
          }
        >
          <i className={`${item.icon} me-2`}></i>
          {item.label}
        </NavLink>
      </div>
    );
  }

  if (!menuData || menuData.length === 0) {
    return (
      <div className="text-center p-3">
        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
        Chargement du menu...
      </div>
    );
  }

  return <>{menuData.map(item => renderMenuItem(item))}</>;
};

export default Sidebar;